import {
  Button,
  Col,
  Flex,
  Icon,
  IconButton,
  Img,
  Row,
  Text,
  Title,
  TyreInfoBox,
} from '@wyz/ui';
import { getTyreIdentifiers, isEmptyValue, useModalState } from '@wyz/utils';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import { PropsWithChildren, useState } from 'react';
import { PrivateComponent } from '~/common/components';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { FEATURES, ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import {
  OfferExtraBonus,
  QuoteProximityStocksTable,
  QuoteStocksTable,
} from '~/purchase/components';
import { IQuoteItemInfo } from '~/purchase/types';
import {
  QUOTE_AVAILABILITY_TRANSLATIONS,
  QuoteHelpers,
} from '~/purchase/helpers';

// eslint-disable-next-line @wyz/module/no-relative-import
import './product-card.scss';

type Props = PropsWithChildren<{
  item: IQuoteItemInfo;
  onShowDetails?: () => void;
  pointOfSaleView?: boolean;
  buyingInfo?: Pick<
    NonNullable<IQuoteItemInfo['pricing']>,
    'buyingPrice' | 'margin' | 'extraBonus'
  >;
  onEditPrice?: () => void;
  requestedQuantity: number;
  customer: IUserCustomer;
  showBfDiscount: boolean;
  currency: string;
  isFallback: boolean;
}>;

function ProductCard({
  item,
  onShowDetails,
  pointOfSaleView = false,
  buyingInfo,
  onEditPrice,
  requestedQuantity,
  showBfDiscount,
  children,
  customer,
  currency,
  isFallback,
}: Props) {
  const { t } = useI18n();
  const { cdnUrl, withCdnUrl } = useConfig();
  const locale = useLocale();
  const acl = useAccessControl();
  const [panel, panelHandlers] = useModalState<'STOCK' | 'PRICE'>();
  const [showAllProximityStocks, setShowAllProximityStocks] = useState(false);

  const availability = QuoteHelpers.computeAvailability({
    requestedQuantity,
    inStockQuantity: item.inStockQuantity,
    availability: item.availability,
    proximityStocks: item.proximityStocks,
    canOrder: acl.isEnabledFeatures([FEATURES.PURCHASE]),
  });
  const isInStock = availability === 'IN_STOCK';
  const isInParentStock = availability === 'IN_PARENT_STOCK';

  const supplierName = isInParentStock
    ? QuoteHelpers.getInStockSupplierName({
        availability,
        stocks: item.stocks,
        requestedQuantity,
      })
    : undefined;

  const handleShowAllProximityStocks = () => {
    setShowAllProximityStocks(true);
    panelHandlers.open('STOCK');
  };
  const handleBackToNormalView = () => {
    setShowAllProximityStocks(false);
  };

  return (
    <div
      className='product-card bg-white'
      key={item.tyre.ean}
      data-cy={`product-card-${item.tyre.ean}`}
    >
      <div className='bg-neutral-grey-1 py-3'>
        <div
          className={clsx(
            'd-flex align-items-center availability gap-1 justify-content-end me-3',
            {
              'cursor-pointer': availability !== 'UNAVAILABLE',
            },
          )}
          onClick={
            availability !== 'UNAVAILABLE'
              ? () => panelHandlers.open('STOCK')
              : undefined
          }
        >
          <Text
            weight='semi-bold'
            heading='sm'
            id={`availability-${item.tyre.ean}`}
            color={isInStock ? 'royal-blue' : 'secondary'}
          >
            {isInParentStock && supplierName ? (
              supplierName
            ) : (
              <Trans i18nKey={QUOTE_AVAILABILITY_TRANSLATIONS[availability]} />
            )}
          </Text>

          <Icon
            size='sm'
            icon='info-circle'
            data-cy={`availability-info-${item.tyre.ean}`}
            className={clsx({
              'text-royal-blue': isInStock,
            })}
          />
        </div>
        <Flex className='product-image' justifyContent='center'>
          <div
            className={clsx({
              'cursor-pointer': onShowDetails !== undefined,
            })}
            onClick={() => onShowDetails?.()}
          >
            <Img
              height={148}
              data-cy={`product-image-${item.tyre.ean}`}
              src={
                item.tyre.image ? `/common/tyres${item.tyre.image}` : undefined
              }
              defaultImage={withCdnUrl('/img/tire-sample.png')}
              baseUrl={cdnUrl}
            />
          </div>
        </Flex>
      </div>
      <div className='product-info-container px-4 py-2'>
        <div className='product-info'>
          <div
            className={clsx('d-flex align-items-end gap-2', {
              'cursor-pointer': onShowDetails !== undefined,
            })}
            onClick={() => onShowDetails?.()}
          >
            <TyreInfoBox
              t={t}
              tyre={{
                brand: item.tyre.brand,
                ean: item.tyre.ean,
                dimension: item.tyre.dimension,
                profile: item.tyre.profile ?? '',
                season: item.tyre.season,
                homologations: item.tyre.homologations,
                prodInfo: item.tyre.prodInfo,
                vehicleType: item.tyre.vehicleType,
                segment: item.tyre.segment,
                ...getTyreIdentifiers(
                  {
                    cai: item.tyre.supplierCode,
                    constructorReference: item.tyre.constructorReference,
                    alternativeReference: item.tyre.alternativeReference,
                  },
                  customer.settingTemplate?.generalSetting
                    .tyreIdentifierFields ?? undefined,
                ),
              }}
              mode='FULL'
              context='quote'
              imageBaseUrl={cdnUrl}
            >
              <Flex className='gap-1 p-2' alignItems='center'>
                <Icon icon='showers-line' size='xs' />
                <Text
                  heading='xs'
                  weight='bold'
                  color='secondary'
                  id={`product-info-wet-grip-${item.tyre.ean}`}
                >
                  {isEmptyValue(item.tyre?.wetGrip) ? '-' : item.tyre.wetGrip}
                </Text>
                <Icon icon='gas-station-line' size='xs' />
                <Text
                  heading='xs'
                  weight='bold'
                  color='secondary'
                  id={`product-info-rolling-resistance-${item.tyre.ean}`}
                >
                  {isEmptyValue(item.tyre?.rollingResistance)
                    ? '-'
                    : item.tyre.rollingResistance}
                </Text>
                <Icon icon='volume-down-line' size='xs' />
                <Text
                  heading='xs'
                  weight='bold'
                  color='secondary'
                  id={`product-info-noise-performance-${item.tyre.ean}`}
                >
                  {isEmptyValue(item.tyre?.noisePerformance)
                    ? '-'
                    : item.tyre.noisePerformance}
                </Text>
              </Flex>
            </TyreInfoBox>
          </div>
          <div className='mt-2'>
            {showBfDiscount && !isNil(item.tyre.bf) && (
              <Row>
                <Text
                  color='secondary-gray'
                  weight='bold'
                  heading='xxs'
                  id={`product-bf-price-${item.tyre.ean}`}
                >
                  <Trans
                    i18nKey='app.purchase.quote_search_page.product.bf'
                    values={{
                      price: locale.formatters.money(item.tyre.bf, {
                        currency: item.pricing?.currency,
                        fallback: '/',
                      }),
                    }}
                  />
                </Text>
              </Row>
            )}
            <Row>
              <Col sm={pointOfSaleView ? 7 : 12}>
                <Flex className='gap-1' alignItems='center'>
                  <Title
                    heading='lg'
                    weight='bold'
                    color='tertiary-bold'
                    id={`product-price-${item.tyre.ean}`}
                  >
                    {isFallback ? (
                      '-'
                    ) : (
                      <Trans
                        i18nKey='app.purchase.quote_search_page.product.price_with_taxes'
                        values={{
                          price: locale.formatters.money(
                            item.pricing?.unitPriceTaxIncl,
                            {
                              fallback: '-',
                              currency: item.pricing?.currency,
                            },
                          ),
                        }}
                      />
                    )}
                  </Title>
                  {showBfDiscount &&
                    !isFallback &&
                    !isNil(item.pricing?.bfDiscount) &&
                    item.pricing?.bfDiscount !== 0 && (
                      <Text
                        color={
                          item.manualDiscount ? 'danger' : 'secondary-gray'
                        }
                        weight='bold'
                        heading='xxs'
                        className='no-wrap'
                        id={`product-bf-discount-${item.tyre.ean}`}
                      >
                        <Trans
                          i18nKey='app.purchase.quote_search_page.product.bf_discount'
                          values={{
                            value: -item.pricing?.bfDiscount,
                          }}
                        />
                      </Text>
                    )}
                  {onEditPrice && (
                    <PrivateComponent
                      roles={[ROLES.ROLE_QUOTE_CART_UPDATE_PRICE]}
                    >
                      <Icon
                        icon='pencil-line'
                        size='xs'
                        className='cursor-pointer'
                        onClick={onEditPrice}
                      />
                    </PrivateComponent>
                  )}
                </Flex>
              </Col>
              {pointOfSaleView && (
                <Col sm={5} className='text-right'>
                  <Flex
                    justifyContent='end'
                    alignItems='center'
                    className='gap-1'
                  >
                    <Text
                      heading='xs'
                      weight='semi-bold'
                      color='secondary-gray'
                      id={`product-buying-price-${item.tyre.ean}`}
                    >
                      <Trans
                        i18nKey='app.purchase.quote_search_page.product.purchase_price'
                        values={{
                          price: locale.formatters.money(
                            isFallback ? null : buyingInfo?.buyingPrice,
                            {
                              fallback: '-',
                              currency: item.pricing?.currency,
                            },
                          ),
                        }}
                      />
                    </Text>
                    {!isFallback && buyingInfo?.extraBonus && (
                      <OfferExtraBonus
                        id={`quote-extra-bonus-${item.tyre.ean}`}
                        extraBonus={buyingInfo.extraBonus}
                      />
                    )}
                  </Flex>
                  {buyingInfo?.buyingPrice != 0 && (
                    <Text
                      heading='xs'
                      weight='semi-bold'
                      color='secondary-gray'
                      id={`product-margin-${item.tyre.ean}`}
                    >
                      <Trans
                        i18nKey='app.purchase.quote_search_page.product.profit'
                        values={{
                          price: locale.formatters.money(
                            isFallback ? null : buyingInfo?.margin,
                            {
                              fallback: '-',
                              currency: item.pricing?.currency,
                            },
                          ),
                        }}
                      />
                    </Text>
                  )}
                </Col>
              )}
            </Row>
          </div>
        </div>
      </div>
      <div className='px-4 py-2 mt-auto'> {children}</div>
      <div
        onClick={panelHandlers.close}
        className={clsx('panel panel-mask', {
          'panel-active': panel !== undefined,
        })}
      ></div>
      <div
        data-cy={`quote-stock-panel-${item.tyre.ean}`}
        className={clsx('panel stock-panel bg-white p-3', {
          'panel-active': panel === 'STOCK',
        })}
      >
        <Flex className='mb-3' justifyContent='between' alignItems='center'>
          {showAllProximityStocks ? (
            <Button
              id='back-to-normal-view'
              variant='link'
              onClick={handleBackToNormalView}
              iconAlign='left'
              icon='arrow-left'
            >
              <Trans i18nKey='app.purchase.quote_search_page.product.stock.back' />
            </Button>
          ) : (
            <Text
              color='secondary-gray'
              heading='sm'
              weight='bold'
              id={`product-details-information-${item.tyre.ean}`}
            >
              <Trans i18nKey='app.purchase.quote_search_page.product.details.information' />
            </Text>
          )}
          <IconButton
            id='close-stock-panel'
            variant='link'
            iconAlign='right'
            icon='close'
            onClick={() => {
              panelHandlers.close();
              setShowAllProximityStocks(false);
            }}
          >
            <Trans i18nKey='app.common.button.close' />
          </IconButton>
        </Flex>
        {!showAllProximityStocks && item.stocks && (
          <QuoteStocksTable
            stocks={item.stocks}
            currency={currency}
            ean={item.tyre.ean}
          />
        )}
        {panel === 'STOCK' && (
          <>
            {customer.hasProximityStocks && (
              <QuoteProximityStocksTable
                proximityStocks={item.proximityStocks}
                onShowAll={handleShowAllProximityStocks}
                showAll={showAllProximityStocks}
                ean={item.tyre.ean}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProductCard;
